import { useEffect, useMemo, useRef, useState } from "react";
import { AttributionControl, Map as CoreMap, MapRef } from "react-map-gl";
import { MapboxOverlay, MapboxOverlayProps } from "@deck.gl/mapbox/typed";
import { useControl } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { MapProps } from "./types";
import { MAPBOX_STYLE, MAPBOX_TOKEN } from "./enums";
import useQueryState from "../../hooks/useQueryState";
import useCountries from "../../hooks/useCountries";
import { GeoJsonLayer } from "@deck.gl/layers/typed";
import type { LayersList } from "@deck.gl/core/typed";
import {
  Box,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { getColorForValue, hexToRGB } from "../../enums/layers";
import useGeoUnitAnomaliesValues from "../../hooks/useGeoUnitAnomaliesValues";
import bbox from "@turf/bbox";
import { Feature, FeatureCollection } from "geojson";
import useGeoUnitHybridValues from "../../hooks/useGeoUnitHybridValues";
import { LAND_USE } from "../../enums/landUse";
import { createPortal } from "react-dom";
import { useUIStore } from "../../store/ui";

type TooltipProps = {
  name: string;
  value?: number;
  rows: { label: string; value: string | number }[];
};

export const MapTooltip = ({ name, value, rows }: TooltipProps) => {
  return (
    <Paper
      elevation={6}
      sx={{
        backgroundColor: "rgba(97, 97, 97, 0.9)",
        color: "#fff",
        borderRadius: 2,
        p: "10px 12px",
        minWidth: 220,
        pointerEvents: "none",
        fontFamily: "Inter, Arial, sans-serif",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={0.5}
      >
        <Typography fontSize={13} fontWeight={600}>
          {name}
        </Typography>

        <Typography fontSize={13} fontWeight={600} sx={{ opacity: 0.9 }}>
          {value != null ? value.toFixed(2) : "N/A"}
        </Typography>
      </Stack>

      <Stack>
        {rows.map((row) => (
          <Stack
            key={row.label}
            direction="row"
            alignItems="center"
            fontSize={11}
          >
            <Typography fontSize={10} whiteSpace="nowrap">
              {row.label}
            </Typography>

            <Divider
              sx={{
                flex: 1,
                mx: 1,
                borderColor: "rgba(255,255,255,0.25)",
              }}
            />

            <Typography fontSize={11} fontWeight={400} whiteSpace="nowrap">
              {row.label === "Population"
                ? row.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                : `${(Number(row.value) * 100).toFixed(2)}%`}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};

function DeckGLOverlay(props: MapboxOverlayProps & { interleaved?: boolean }) {
  const overlay = useControl<MapboxOverlay>(() => new MapboxOverlay(props));
  overlay.setProps({ ...props, getCursor: () => "crosshair" });
  return null;
}

const Map = ({
  layers = [],
  viewportState,
  onViewportChange,
  children,
  width = "100vw",
  height,
  season,
}: MapProps) => {
  const mapRef = useRef<MapRef | null>(null);
  const mapContainer = mapRef.current?.getMap()?.getContainer();
  const isLandCoverMasked = useUIStore((s) => s.isLandCoverMasked);

  const mapWidth = mapContainer?.clientWidth ?? 0;
  const mapHeight = mapContainer?.clientHeight ?? 0;
  const [hoverInfo, setHoverInfo] = useState<any>(null);
  const TOOLTIP_WIDTH = 240; // match your MUI tooltip width
  const TOOLTIP_HEIGHT = 180;
  const OFFSET = 12;
  const shouldFlipUp = hoverInfo?.y + TOOLTIP_HEIGHT + OFFSET > mapHeight;

  const shouldFlipLeft = hoverInfo?.x + TOOLTIP_WIDTH + OFFSET > mapWidth;
  const translateX = shouldFlipLeft
    ? `calc(-100% - ${OFFSET}px)`
    : `${OFFSET}px`;

  const translateY = shouldFlipUp ? `calc(-100% - ${OFFSET}px)` : `${OFFSET}px`;
  const {
    queryState: {
      country,
      aggregationLevel,
      year,
      geographicalUnit,
      layers: queryLayers,
      ui: { mapSidebar, mainSidebar },
    },
    setQueryState,
  } = useQueryState();
  const {
    getAnomaliesGeographicalUnitValues: {
      data: anomalyGeoUnitValues,
      isFetching: isLoadingAnomalyGeoUnit,
      isError: isErrorAnomaly,
      isLoading: isLoadingAnomaly,
      isFetched: isFetchedAnomalyGeoUnit,
    },
  } = useGeoUnitAnomaliesValues({
    layers,
    season: season ?? "",
    isEnabled: layers[0]?.displayName !== "Hybrid Index",
  });
  const {
    getHybridGeographicalUnitValues: {
      data: hybridGeoUnitValues,
      isFetching: isLoadingHybridGeoUnit,
      isError: isErrorHybrid,
      isLoading: isLoadingHybrid,
      isFetched: isFetchedHybrid,
    },
  } = useGeoUnitHybridValues({
    layers,
    season: season ?? "",
    isEnabled: layers[0]?.displayName === "Hybrid Index",
  });
  const {
    getCountryAdministrativeUnit: {
      data: geoUnit,
      isFetching: isLoadingCountry,
    },
  } = useCountries({ country, aggregationLevel });
  const [isLoading, setIsLoading] = useState(
    isLoadingCountry || isLoadingAnomalyGeoUnit || isLoadingHybridGeoUnit,
  );

  const bounds = useMemo(() => {
    if (!geoUnit?.features?.length) return undefined;
    const featureCollection: FeatureCollection = {
      type: "FeatureCollection",
      features: geoUnit.features as Feature[],
    };

    return bbox(featureCollection);
  }, [
    geoUnit,
    country,
    aggregationLevel,
    layers[0]?.displayName === "Hybrid Index"
      ? hybridGeoUnitValues
      : anomalyGeoUnitValues,
    season,
  ]);

  const filteredAnomalyGeounitValues = useMemo(() => {
    const results: Record<string, number> = {};

    if (anomalyGeoUnitValues?.data) {
      anomalyGeoUnitValues.data
        .filter((item: any) => new Date(item[0]).getFullYear() === year)
        .forEach((item: any[]) => {
          results[item[2]] = item[1];
        });
    }

    return results;
  }, [anomalyGeoUnitValues?.data, year]);

  useEffect(() => {
    if (!bounds) return;

    mapRef.current?.fitBounds(
      [
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]],
      ],
      {
        maxZoom: 8,
        duration: 1000,
      },
    );
  }, [bounds]);

  const geoJsonLayer = useMemo(
    () =>
      country &&
      aggregationLevel &&
      geoUnit &&
      (isFetchedAnomalyGeoUnit || isFetchedHybrid)
        ? new GeoJsonLayer({
            id: `geojson-layer-${season}-${year}-${JSON.stringify(layers)}-${isLandCoverMasked}`,
            data: geoUnit.features,
            pickable: true,
            stroked: true,
            filled: true,
            lineWidthMinPixels: 1,
            getFillColor: (feature: any) => {
              const id = feature?.properties?.location_id;
              const avg =
                layers[0]?.displayName === "Hybrid Index"
                  ? hybridGeoUnitValues?.[id]
                  : filteredAnomalyGeounitValues?.[id];
              const hex = getColorForValue(avg);
              return hex ? hexToRGB(hex) : [193, 193, 193, 255];
            },
            onClick: (info: any) => {
              if (!info.object) return;
              const geographicalUnitId = info?.object.properties?.location_id;
              if (geographicalUnitId) {
                setQueryState({ geographicalUnit: geographicalUnitId });
              }
            },
            onHover: (info) => {
              if (info.object) {
                setHoverInfo(info);
              } else {
                setHoverInfo(null);
              }
            },
            getLineColor: [180, 180, 180, 255],
            autoHighlight: true,
            highlightColor: [255, 255, 0, 180],
          })
        : null,
    [
      geoUnit,
      country,
      aggregationLevel,
      year,
      hybridGeoUnitValues,
      season,
      filteredAnomalyGeounitValues,
      setQueryState,
      isFetchedAnomalyGeoUnit,
      isFetchedHybrid,
    ],
  );

  const borderLayer = useMemo(
    () =>
      country &&
      aggregationLevel &&
      geoUnit &&
      (isFetchedAnomalyGeoUnit || isFetchedHybrid)
        ? new GeoJsonLayer({
            id: `geojson-layer-${layers[0]?.displayName}-${season}-${year}-${geographicalUnit}-${isLandCoverMasked}`,
            data: geoUnit.features.find(
              (item) => item.properties?.location_id === geographicalUnit,
            ),
            pickable: false,
            stroked: true,
            filled: false,
            getLineColor: [255, 0, 0, 255],
            lineWidthMinPixels: 1,
          })
        : null,
    [
      geoUnit,
      country,
      aggregationLevel,
      year,
      layers,
      season,
      geographicalUnit,
      isFetchedAnomalyGeoUnit,
      isFetchedHybrid,
    ],
  );

  const allLayers = useMemo<LayersList>(
    () => (geoJsonLayer ? [geoJsonLayer] : []),
    [geoJsonLayer, year, geographicalUnit, isLandCoverMasked],
  );

  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current.getMap();

    // requestAnimationFrame(() => {
    //   map.resize();
    // });

    const timeout = setTimeout(() => {
      map.resize();
    }, 300);

    return () => clearTimeout(timeout);
  }, [mapSidebar, mainSidebar, width, height, queryLayers]);

  useEffect(() => {
    const handleResize = () => {
      mapRef.current?.getMap()?.resize();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getFilterMap = useMemo(() => {
    if (isErrorAnomaly || isErrorHybrid) return true;
    if (
      layers[0]?.displayName === "Hybrid Index" &&
      !hybridGeoUnitValues &&
      !isLoadingHybrid &&
      Object.keys(filteredAnomalyGeounitValues).length === 0
    )
      return true;
    if (
      layers[0]?.displayName !== "Hybrid Index" &&
      !anomalyGeoUnitValues &&
      !isLoadingAnomaly &&
      Object.keys(filteredAnomalyGeounitValues).length === 0
    )
      return true;
    return false;
  }, [
    geoUnit,
    country,
    aggregationLevel,
    layers[0]?.displayName === "Hybrid Index"
      ? hybridGeoUnitValues
      : anomalyGeoUnitValues,
    season,
  ]);

  const buildLandUseRows = (props: any) =>
    LAND_USE.flatMap(({ key, label }) => {
      const v = props[key];
      if (v == null) return [];

      return [
        {
          label,
          value: v,
        },
      ];
    });

  const mapRect = mapContainer?.getBoundingClientRect();

  const screenX = mapRect ? mapRect.left + hoverInfo?.x : hoverInfo?.x;
  const screenY = mapRect ? mapRect.top + hoverInfo?.y : hoverInfo?.y;

  return (
    <CoreMap
      id="mainMap"
      ref={mapRef}
      {...viewportState}
      mapStyle={MAPBOX_STYLE}
      mapboxAccessToken={MAPBOX_TOKEN}
      cursor="crosshair"
      onMove={(evt) => onViewportChange(evt.viewState)}
      onMoveStart={() => setIsLoading(true)}
      onMoveEnd={() => setIsLoading(false)}
      onZoomStart={() => setIsLoading(true)}
      onZoomEnd={() => setIsLoading(false)}
      style={{
        width,
        height,
        // filter: getFilterMap ? "grayscale(100%) brightness(0.9)" : undefined,
      }}
      attributionControl={false}
    >
      {hoverInfo &&
        mapContainer &&
        createPortal(
          <Stack
            style={{
              position: "fixed",
              pointerEvents: "none",
              left: screenX,
              top: screenY,
              transform: `translate(${translateX}, ${translateY})`,
              zIndex: 999999,
            }}
          >
            <MapTooltip
              name={
                hoverInfo.object.properties?.geounit ??
                hoverInfo.object.properties?.location_id ??
                ""
              }
              value={
                layers[0]?.displayName === "Hybrid Index"
                  ? hybridGeoUnitValues?.[
                      hoverInfo.object.properties?.location_id
                    ]
                  : filteredAnomalyGeounitValues?.[
                      hoverInfo.object.properties?.location_id
                    ]
              }
              rows={buildLandUseRows(hoverInfo.object.properties)}
            />
          </Stack>,
          document.body,
        )}

      <AttributionControl position="top-right" />
      {children}
      {(isLoading ||
        isLoadingCountry ||
        isLoadingAnomalyGeoUnit ||
        isLoadingHybridGeoUnit) && (
        <Box sx={{ position: "absolute", zIndex: 10, top: "0", width: "100%" }}>
          <LinearProgress />
        </Box>
      )}
      {/* {(isErrorAnomaly || isErrorHybrid) && (
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{
            position: "absolute",
            zIndex: 10,
            width: "100%",
            height: "15%",
          }}
        >
          <Paper sx={{ padding: 1 }}>
            <Typography variant="subtitle2">No Data</Typography>
          </Paper>
        </Stack>
      )} */}
      <DeckGLOverlay
        layers={[...allLayers, borderLayer]}
        interleaved={true}
        //       getTooltip={({ object }) => {
        //         if (!object) return null;

        //         const props = (object as any).properties ?? {};
        //         const name = props.geounit ?? props.location_id ?? "";
        //         const id = object.properties?.location_id;

        //         const value =
        //           layers[0]?.displayName === "Hybrid Index"
        //             ? hybridGeoUnitValues?.[id]
        //             : filteredAnomalyGeounitValues?.[id];

        //         const landUseRows = LAND_USE.map(({ key, label }) => {
        //           const v = props[key];

        //           if (v == null) return "";

        //           return tooltipRow(label, `${v}${key === "population" ? "" : "%"}`);
        //         }).join("");

        //         return {
        //           style: {
        //             pointerEvents: "none",
        //             background: "transparent",
        //             padding: "0px",
        //             borderRadius: "0px",
        //           },
        //           html: `
        //      <div style="
        //   background: rgba(97, 97, 97, 0.90);
        //   color: #ffffff;
        //   border-radius: 10px;
        //   padding: 10px 12px;
        //   min-width: 220px;
        //   font-family: Inter, Arial, sans-serif;
        //   box-shadow: 0 6px 16px rgba(0,0,0,0.35);
        // ">
        //       <!-- Title -->
        //      <div style="
        //       display: flex;
        //       align-items: center;
        //       justify-content: space-between;
        //       font-weight: 600;
        //       font-size: 13px;
        //       margin-bottom: 8px;
        //     ">
        //       <div>${name}</div>
        //       <div style="opacity: 0.9;">
        //         ${value?.toFixed(2) ?? "N/A"}
        //       </div>
        //     </div>

        //       <!-- Row -->
        //       ${landUseRows}
        //     </div>
        //   `,
        //         };
        //       }}
      />
    </CoreMap>
  );
};

export default Map;
